import { useContext, useMemo } from 'react'

import {
  ARTICLE_ANALYSIS_CONDITION_CLEAR,
  useArticleAnalysisSearchCondition,
  useBatchDiagnoseArticlesApi
} from '@/common/api/modules/articles'
import { ArticleBatchDiagnoseItem } from '@/common/api/params/articles'
import { isSameSearchCondition, useFirstLoading } from '@/common/api/search'
import { articleDetailPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { WidgetChoice } from '@/common/form/choice'

import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import Line from '@/components/view/layouts/area/Line'
import SearchConditionAreaContent from '@/components/view/layouts/area/SearchConditionAreaContent'
import SortButtonList from '@/components/view/layouts/button/SortButtonList'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import DisclosureSortRadioSelect from '@/components/view/molecules/disclosure/DisclosureSortRadioSelect'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'
import ArticleDiagnosisModalProvider, {
  ArticleDiagnosisModalContext
} from '@/components/container/modal/article/ArticleDiagnosisModalProvider'

const PRIORITY_CHOICES: WidgetChoice<string>[] = [
  { name: '高', value: 'high' },
  { name: '中', value: 'medium' },
  { name: '低', value: 'low' }
]

const priorityLabel = (priority: string): string =>
  priority === 'high' ? '高' : priority === 'medium' ? '中' : '低'

const priorityTextColor = (priority: string): TextColor =>
  priority === 'high'
    ? TextColor.CRITICAL
    : priority === 'medium'
      ? TextColor.CAUTION
      : TextColor.SECONDARY

// インパクト・工数から改善の着手しやすさを4象限に分類する（表示用の集計のみ。優先度バッジとは別軸）
const IMPACT_EFFORT_THRESHOLD = 50

interface QuadrantCardProps {
  label: string
  subText: string
  count: number
  color: TextColor
}

const QuadrantCard = ({ label, subText, count, color }: QuadrantCardProps) => (
  <SummaryItem>
    <div className='flex items-center gap-2 pb-1'>
      <Text size={12} weight='bold'>
        {label}
      </Text>
      <Text size={10} color={TextColor.SECONDARY}>
        {subText}
      </Text>
    </div>
    <Text size={24} weight='bold' color={color}>
      {count}
    </Text>
  </SummaryItem>
)

const QuadrantSummary = ({ items }: { items: ArticleBatchDiagnoseItem[] }) => {
  const topPriority = items.filter(
    (i) => i.impact >= IMPACT_EFFORT_THRESHOLD && i.effort < IMPACT_EFFORT_THRESHOLD
  ).length
  const recommended = items.filter(
    (i) => i.impact >= IMPACT_EFFORT_THRESHOLD && i.effort >= IMPACT_EFFORT_THRESHOLD
  ).length
  const niceToHave = items.filter(
    (i) => i.impact < IMPACT_EFFORT_THRESHOLD && i.effort < IMPACT_EFFORT_THRESHOLD
  ).length
  const skip = items.filter(
    (i) => i.impact < IMPACT_EFFORT_THRESHOLD && i.effort >= IMPACT_EFFORT_THRESHOLD
  ).length

  return (
    <SummaryItemList>
      <QuadrantCard
        label='最優先'
        subText='高インパクト×低工数'
        count={topPriority}
        color={TextColor.CRITICAL}
      />
      <QuadrantCard
        label='推奨'
        subText='高インパクト×高工数'
        count={recommended}
        color={TextColor.CAUTION}
      />
      <QuadrantCard
        label='できれば'
        subText='低インパクト×低工数'
        count={niceToHave}
        color={TextColor.SUCCESS}
      />
      <QuadrantCard
        label='見送り'
        subText='低インパクト×高工数'
        count={skip}
        color={TextColor.SECONDARY}
      />
    </SummaryItemList>
  )
}

interface RowProps {
  siteId: number
  item: ArticleBatchDiagnoseItem
}

const ArticleAnalysisRow = ({ siteId, item }: RowProps) => {
  const { showModal: showArticleDiagnosis } = useContext(ArticleDiagnosisModalContext)

  return (
    <TableRow>
      <TableRowItem width={64}>
        <Text size={14} weight='bold' color={priorityTextColor(item.priority)}>
          {priorityLabel(item.priority)}
        </Text>
      </TableRowItem>
      <TableRowItem>
        <TextButton link={articleDetailPath(siteId, item.article_proposal_id)}>
          {item.title || '（無題）'}
        </TextButton>
      </TableRowItem>
      <TableRowItem width={64}>
        <Text size={14}>{item.seo_score}</Text>
      </TableRowItem>
      <TableRowItem width={64}>
        <Text size={14}>{item.impact}</Text>
      </TableRowItem>
      <TableRowItem width={64}>
        <Text size={14}>{item.effort}</Text>
      </TableRowItem>
      <TableRowItem width={160}>
        <TextButton
          arrow
          onClick={() =>
            showArticleDiagnosis({ siteId, articleProposalId: item.article_proposal_id })
          }
        >
          詳細に診断する
        </TextButton>
      </TableRowItem>
    </TableRow>
  )
}

// 記事診断結果一覧：全記事をルールベースで高速スコアリングし、SEOスコア・改善優先度・インパクト・
// 予想工数を一覧化する（AIは使用せず高速算出）。個別記事のAI診断は行から実行する。
const ArticleAnalysisListPage = () => {
  const siteId = useSiteId()
  const [condition, setCondition] = useArticleAnalysisSearchCondition()
  const { data, isLoading, mutate } = useBatchDiagnoseArticlesApi({ site_id: siteId })
  const isFirstLoading = useFirstLoading(isLoading)
  const isDefault = isSameSearchCondition(condition, ARTICLE_ANALYSIS_CONDITION_CLEAR)

  const filteredItems = useMemo(() => {
    const items = data?.items ?? []
    return items.filter(
      (item) =>
        (!condition.priority || item.priority === condition.priority) &&
        (!condition.title || item.title.includes(condition.title))
    )
  }, [data, condition.priority, condition.title])

  return (
    <ArticleDiagnosisModalProvider>
      <Main type={MainLayoutType.SMALL}>
        <PageTitle
          title='AI一括診断結果一覧'
          badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
          description='全記事をルールベースで高速スコアリングし、SEOスコア・改善優先度・インパクト・予想工数を一覧化します（AIは使用せず高速算出）。個別記事のAI診断は一覧から実行できます。'
          rightNode={
            <AiButton size='large' onClick={() => mutate()} loading={isLoading}>
              再診断する
            </AiButton>
          }
        />

        <Content>
          {isFirstLoading ? (
            <PageListLoading />
          ) : (
            <>
              <div>
                <Area>
                  <QuadrantSummary items={data?.items ?? []} />
                </Area>
                <FormCaption size={12}>
                  対象は全87記事。詳細なAI診断（指摘事項・修正案の生成）は記事ごとに「より詳細な診断を行う」から実行してください。
                </FormCaption>
              </div>
              <Area tableWrap noBorder>
                <SearchConditionAreaContent>
                  <div className='flex w-full items-center justify-between gap-2'>
                    <SortButtonList>
                      <DisclosureSortRadioSelect
                        name='priority'
                        currentValue={condition.priority}
                        choices={PRIORITY_CHOICES}
                        setCondition={setCondition}
                      >
                        優先度
                      </DisclosureSortRadioSelect>
                      <Button
                        color={ButtonColor.SECONDARY}
                        disabled={isDefault}
                        onClick={() => setCondition(ARTICLE_ANALYSIS_CONDITION_CLEAR)}
                      >
                        条件をリセット
                      </Button>
                    </SortButtonList>
                    <TextField
                      type='search'
                      size={TextBoxSize.LARGE}
                      value={condition.title ?? ''}
                      onChange={(e) =>
                        setCondition((c) => ({ ...c, page: 1, title: e.target.value }))
                      }
                      placeholder='記事タイトルで検索する'
                    />
                  </div>
                </SearchConditionAreaContent>
                {filteredItems.length === 0 ? (
                  <NoResultGuidance
                    searched={!isDefault}
                    name='記事'
                    onClickReset={
                      !isDefault ? () => setCondition(ARTICLE_ANALYSIS_CONDITION_CLEAR) : undefined
                    }
                  />
                ) : (
                  <table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem type='thin'>
                          <Text size={12}>優先度</Text>
                        </TableHeadItem>
                        <TableHeadItem type='thin'>
                          <Text size={12}>記事タイトル</Text>
                        </TableHeadItem>
                        <TableHeadItem width={64} type='thin'>
                          <Text size={12}>SEOスコア</Text>
                        </TableHeadItem>
                        <TableHeadItem width={64} type='thin'>
                          <Text size={12}>インパクト</Text>
                        </TableHeadItem>
                        <TableHeadItem width={64} type='thin'>
                          <Text size={12}>予想工数</Text>
                        </TableHeadItem>
                        <TableHeadItem width={160} type='thin'>
                          <Text size={12}>操作</Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {filteredItems.map((item) => (
                        <ArticleAnalysisRow
                          key={item.article_proposal_id}
                          siteId={siteId}
                          item={item}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
                <Line />
                <div className='px-3 py-2'>
                  <FormCaption size={12}>
                    SEOスコア／インパクト／予想工数はいずれも0〜100のルールベーススコア（値が大きいほど良好・影響大・工数大）。改善優先度はインパクトと工数から自動算出（高=優先度スコア60以上／中=40〜59／低=40未満）。
                  </FormCaption>
                </div>
              </Area>
            </>
          )}
        </Content>
      </Main>
    </ArticleDiagnosisModalProvider>
  )
}

export default ArticleAnalysisListPage
